import {
    ChangeDetectorRef,
    Component,
    forwardRef,
    inject,
    input,
    model,
    OnChanges,
    OnDestroy,
    OnInit,
    output,
    SimpleChanges
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormsModule,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators
} from '@angular/forms';
import { validatePhoneFactory } from '@trade-platform/ui-utils';
import { Subscription } from 'rxjs';
import { isString } from 'lodash-es';
import { CountryCode } from '../aix-country-code-dropdown/country-codes';
import { AixNumberComponent } from '../aix-number/aix-number.component';
import { AixDataTestingDirective } from '../../directives/data-testing/data-testing.directive';
import { AixPhoneComponent } from '../aix-phone/aix-phone.component';
import { AixCountryCodeDropdownComponent } from '../aix-country-code-dropdown/aix-country-code-dropdown';

export interface IntlPhoneValue {
    countryCode: string;
    phoneNumber: string;
}

@Component({
    selector: 'aix-intl-phone',
    styleUrls: ['./aix-intl-phone.component.scss'],
    templateUrl: './aix-intl-phone.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => AixIntlPhoneComponent)
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: AixIntlPhoneComponent
        }
    ],
    standalone: true,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        AixCountryCodeDropdownComponent,
        AixPhoneComponent,
        AixDataTestingDirective,
        AixNumberComponent
    ]
})
export class AixIntlPhoneComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
    private _fb = inject(UntypedFormBuilder);
    private cd = inject(ChangeDetectorRef);

    config = input<any>();
    isStandalone = input<boolean>(true);
    isRequired = input<boolean>(false);
    isDisabled = input<boolean>(false);
    isValid = input<boolean>(false);
    isDirty = model<boolean>(false);
    label = input<string>('Phone Number');
    initialValue = input<IntlPhoneValue>();

    valueChanges = output<IntlPhoneValue>();
    setDirty = output();

    _initialValue: IntlPhoneValue = {
        countryCode: '1',
        phoneNumber: ''
    };
    form: UntypedFormGroup;

    showProgress = false;
    subscriptions: Subscription[] = [];

    private _onChange: (value: any | null | undefined) => void;
    _propagateChanges: (value: string) => void = () => ({});

    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);

    constructor() {
        this._createFormGroup();
        this._setupObservables();
    }

    ngOnInit() {
        this.form.patchValue({});
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.initialValue) {
            if (isString(this.initialValue())) {
                this._initialValue.phoneNumber = this.initialValue() as unknown as string;
            } else if (this.initialValue()) {
                this._initialValue.phoneNumber = (
                    this.initialValue() as IntlPhoneValue
                ).phoneNumber;
                this._initialValue.countryCode = (
                    this.initialValue() as IntlPhoneValue
                ).countryCode;
            }

            this.form.patchValue({
                phoneNumber: this._initialValue.phoneNumber,
                countryCode: this._initialValue.countryCode
            });
            this.form.updateValueAndValidity();
            this.cd.detectChanges();
        }

        if (changes.isRequired) {
            const phoneNumber: AbstractControl | null = this.form.get('phoneNumber');

            if (this.isRequired()) {
                if (this._initialValue.countryCode !== '1') {
                    phoneNumber?.setValidators([
                        Validators.required,
                        Validators.pattern(/^[0-9]+$/)
                    ]);
                } else {
                    phoneNumber?.setValidators([Validators.required, validatePhoneFactory()]);
                }
            } else {
                if (this._initialValue.countryCode !== '1') {
                    phoneNumber?.setValidators([Validators.pattern(/^[0-9]+$/)]);
                } else {
                    phoneNumber?.setValidators([validatePhoneFactory()]);
                }
            }

            this.form.get('phoneNumber')?.updateValueAndValidity();
            this.form.updateValueAndValidity();
            this.cd.detectChanges();
        }
    }

    onUserInput(value: string) {
        if (!this.isDirty()) {
            this.setDirty.emit();

            // It's standalone, we set the dirty state here instead through the store
            if (this.isStandalone()) {
                this.isDirty.set(true);
            }
        }
        this._propagateChanges(value);
    }

    onUserChangesCountryCode(code: CountryCode) {
        const phoneNumber: string =
            code.value === '1'
                ? this.form.get('phoneNumber')?.value.slice(0, 10)
                : this.form.get('phoneNumber')?.value;
        const newValue = {
            phoneNumber: phoneNumber,
            countryCode: code.value
        };

        this._initialValue.countryCode = code.value;
        this.form.patchValue(newValue);
        this.form.updateValueAndValidity();
        this.valueToSend(newValue);
        this.cd.detectChanges();
    }

    valueToSend(rawValue: IntlPhoneValue | string) {
        if (isString(rawValue)) {
            this._initialValue.phoneNumber = rawValue;
        } else {
            this._initialValue.phoneNumber = rawValue.phoneNumber;
            this._initialValue.countryCode = rawValue.countryCode;
        }

        this.valueChanges.emit(this._initialValue);
        return this._initialValue;
    }

    validate(ctrl: UntypedFormControl) {
        const isValid = this.form.valid ? null : { invalid: true };

        if (this._initialValue.countryCode === '1') {
            return !ctrl.value || ctrl.value.phoneNumber?.match(/\d\d\d\d\d\d\d\d\d\d/g)
                ? null
                : isValid;
        } else {
            return isValid;
        }
    }

    writeValue(value: any | null | undefined): void {
        this.form.setValue({
            countryCode: value?.countryCode ? value.countryCode : '1',
            phoneNumber: value?.phoneNumber ? value.phoneNumber : ''
        });
        this.form.updateValueAndValidity();
    }

    registerOnChange(fn: (value: any | null | undefined) => void): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: any): void {
        // TODO: implement this method
        // throw new Error('registerOnTouched not implemented');
    }

    private _createFormGroup() {
        this.form = this._fb.group({
            countryCode: this._fb.control('1', []),
            phoneNumber: this._fb.control(null, [Validators.required, validatePhoneFactory()])
        });
    }

    private _setupObservables() {
        this.subscriptions.push(
            this.form.valueChanges.subscribe(value => {
                const phoneNumber: AbstractControl | null = this.form.get('phoneNumber');

                if (value.countryCode && value.countryCode !== this._initialValue.countryCode) {
                    this._initialValue.countryCode = value.countryCode;
                    if (value.countryCode !== '1') {
                        phoneNumber?.setValidators([
                            Validators.required,
                            Validators.pattern(/^[0-9]+$/)
                        ]);
                    } else {
                        phoneNumber?.setValidators([Validators.required, validatePhoneFactory()]);
                    }
                    this.form.get('phoneNumber')?.updateValueAndValidity();
                }

                if (
                    isString(value.phoneNumber) &&
                    value.phoneNumber !== this._initialValue.phoneNumber
                ) {
                    this._initialValue.phoneNumber = value.phoneNumber;
                    this.valueToSend(value);
                }

                if (this._onChange) {
                    this._onChange(value);
                }
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }
}
